import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    Label,
    FormGroup,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    Pagination,
    PaginationItem,
    PaginationLink,
    CardFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from "reactstrap";
import classnames from "classnames";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
    getAllCreators,
    createCreator,
    updateCreator,
    deleteCreator,
    toggleCreatorStatus,
    getAllCreatorRequests,
    updateCreatorRequestStatus,
    getCreatorTypeCodes,
    getReputationConfig,
    updateReputationConfig,
} from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    },
    tabNav: {
        cursor: "pointer",
        padding: "12px 24px",
        borderRadius: "10px 10px 0 0",
        fontSize: "14px",
        fontWeight: 600,
        transition: "all 0.2s ease",
    },
    tableRow: {
        transition: "background 0.2s ease",
    },
    infoBlock: {
        background: "#f8f9fe",
        borderRadius: "10px",
        padding: "14px 16px",
        marginBottom: "12px",
        border: "1px solid #eef1f8",
    },
};

const getStatusBadge = (status) => {
    const map = {
        pending: { color: "#fb6340", bg: "rgba(251,99,64,0.1)", label: "Pending" },
        approved: { color: "#2dce89", bg: "rgba(45,206,137,0.1)", label: "Approved" },
        rejected: { color: "#f5365c", bg: "rgba(245,54,92,0.1)", label: "Rejected" },
    };
    const s = map[(status || "").toLowerCase()] || map.pending;
    return (
        <Badge
            style={{
                background: s.bg,
                color: s.color,
                padding: "6px 14px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 700,
                border: `1px solid ${s.color}20`,
            }}
        >
            {s.label}
        </Badge>
    );
};

const Creator = () => {
    const [activeTab, setActiveTab] = useState("types");

    // ── Creator Types State ──
    const [creators, setCreators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ type: "", type_name: "" });
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;
    const [typeCodes, setTypeCodes] = useState([]);
    const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
    const typeDropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
                setTypeDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ── Creator Requests State ──
    const [creatorRequests, setCreatorRequests] = useState([]);
    const [crLoading, setCrLoading] = useState(false);
    const [crError, setCrError] = useState(null);
    const [crStatusFilter, setCrStatusFilter] = useState("All");
    const [crSearch, setCrSearch] = useState("");
    const [crPage, setCrPage] = useState(1);
    const [crTotalPages, setCrTotalPages] = useState(1);
    const [crTotal, setCrTotal] = useState(0);
    const CR_LIMIT = 10;
    const [crViewModal, setCrViewModal] = useState(false);
    const [selectedCreatorReq, setSelectedCreatorReq] = useState(null);

    // ── Creator Eligibility Config State ──
    const [savingConfig, setSavingConfig] = useState(false);
    const [creatorConfig, setCreatorConfig] = useState(null);
    const [tierConfig, setTierConfig] = useState([]);
    const [configErrors, setConfigErrors] = useState({});

    const fetchCreatorConfig = async () => {
        try {
            const res = await getReputationConfig();
            if (res.status && res.payload.configs) {
                const tConfig = res.payload.configs.find(c => c.configKey === "TIER_LIMITS");
                const cConfig = res.payload.configs.find(c => c.configKey === "CREATOR_ELIGIBILITY_CONFIG");

                if (tConfig) setTierConfig(tConfig.configValue);
                if (cConfig) {
                    setCreatorConfig(cConfig.configValue);
                } else {
                    setCreatorConfig({
                        minReputationScore: 20,
                        minReputationTier: "Newbie",
                        requireKyc: false,
                        requireBadge: false,
                        minJoinedRooms: 3,
                        minCreatedPosts: 2
                    });
                }
            }
        } catch (err) {
            console.error("Error fetching creator config:", err);
            Swal.fire("Error", "Failed to fetch creator configuration", "error");
        }
    };

    const saveCreatorConfig = async () => {
        let errors = {};
        if (creatorConfig.minReputationScore < 0) errors.minReputationScore = "Negative values are not allowed";
        if (creatorConfig.minJoinedRooms < 0) errors.minJoinedRooms = "Negative values are not allowed";
        if (creatorConfig.minCreatedPosts < 0) errors.minCreatedPosts = "Negative values are not allowed";

        setConfigErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setSavingConfig(true);
        try {
            const res = await updateReputationConfig({
                configKey: "CREATOR_ELIGIBILITY_CONFIG",
                configValue: creatorConfig,
                description: "Creator eligibility requirements"
            });
            if (res.status) {
                Swal.fire({
                    title: "Success",
                    text: "Creator eligibility thresholds updated successfully",
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            console.error("Error saving creator config:", err);
            Swal.fire("Error", "Failed to update creator eligibility config", "error");
        } finally {
            setSavingConfig(false);
        }
    };

    // ── Creator Types Methods ──
    const toggle = () => {
        setModal(!modal);
        if (modal) {
            setEditingItem(null);
            setFormData({ type: "", type_name: "" });
        }
    };

    const fetchTypeCodes = async (creatorsData) => {
        try {
            const res = await getCreatorTypeCodes();
            if (res.success && res.data && res.data.length > 0) {
                setTypeCodes(res.data);
                return;
            }
        } catch (error) {
            console.error("API type codes not available, using fallback:", error);
        }

        // Fallback: extract distinct types from creators data
        const data = creatorsData || creators;
        if (data && data.length > 0) {
            const seen = new Set();
            const codes = [];
            for (const item of data) {
                if (item.type) {
                    const upper = item.type.toUpperCase();
                    if (!seen.has(upper)) {
                        seen.add(upper);
                        codes.push({ code: upper, label: upper });
                    }
                }
            }
            codes.sort((a, b) => a.label.localeCompare(b.label));
            setTypeCodes(codes);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getAllCreators(page, limit);
            if (res.success === true) {
                const data = res.data || [];
                setCreators(data);
                setTotal(res.pagination?.totalItems || 0);
                setTotalPages(res.pagination?.totalPages || 1);
                // Refresh type codes with the latest creators data
                fetchTypeCodes(data);
            }
        } catch (error) {
            console.error("Error fetching creators:", error);
            Swal.fire("Error", "Failed to load creator types", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchCreatorConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.type) {
            return Swal.fire({ title: "Validation Error", text: "Please select a Type Code.", icon: "warning", timer: 3000, showConfirmButton: false });
        }
        if (!formData.type_name || !formData.type_name.trim()) {
            return Swal.fire({ title: "Validation Error", text: "Please enter a Type Name.", icon: "warning", timer: 3000, showConfirmButton: false });
        }

        try {
            const payload = {
                ...formData,
                type: formData.type.toUpperCase()
            };
            if (editingItem) {
                const res = await updateCreator(editingItem.c_uuid || editingItem._id, payload);
                if (res.success) {
                    Swal.fire({ title: "Success", text: res.message, icon: "success", timer: 3000, showConfirmButton: false });
                    fetchData();
                    fetchTypeCodes();
                }
            } else {
                const res = await createCreator(payload);
                if (res.success) {
                    Swal.fire({ title: "Success", text: res.message, icon: "success", timer: 3000, showConfirmButton: false });
                    fetchData();
                    fetchTypeCodes();
                }
            }
            toggle();
        } catch (error) {
            Swal.fire("Error", error.response?.data?.error || "Failed to save creator type", "error");
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({ type: item.type, type_name: item.type_name });
        setModal(true);
    };

    const handleToggleStatus = async (id) => {
        try {
            const res = await toggleCreatorStatus(id);
            if (res.success) {
                setCreators(prev => prev.map(item =>
                    (item.c_uuid === id || item._id === id) ? { ...item, type_isactive: !item.type_isactive } : item
                ));
            }
        } catch (error) {
            Swal.fire("Error", "Failed to update status", "error");
        }
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete Creator Type?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await deleteCreator(id);
                if (res.success) {
                    Swal.fire({ title: "Deleted!", text: res.message, icon: "success", timer: 3000, showConfirmButton: false });
                    fetchData();
                }
            } catch (error) {
                Swal.fire("Error", "Failed to delete creator type", "error");
            }
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <PaginationItem key={i} active={page === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); setPage(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination className="pagination justify-content-end mb-0">
                <PaginationItem disabled={page === 1}>
                    <PaginationLink onClick={() => setPage(page - 1)} tabIndex="-1"><i className="fas fa-angle-left" /></PaginationLink>
                </PaginationItem>
                {pages}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink onClick={() => setPage(page + 1)}><i className="fas fa-angle-right" /></PaginationLink>
                </PaginationItem>
            </Pagination>
        );
    };

    // ── Creator Requests Methods ──
    const fetchCreatorRequests = async (pg = 1, search = "", status = "All") => {
        setCrLoading(true);
        setCrError(null);
        try {
            const statusParam = status === "All" ? "" : status.toLowerCase();
            const res = await getAllCreatorRequests(pg, CR_LIMIT, search, statusParam);
            if (res?.success && res?.payload) {
                setCreatorRequests(res.payload.creatorRequests || []);
                setCrTotal(res.payload.total || 0);
                setCrTotalPages(res.payload.totalPages || 1);
                setCrPage(res.payload.page || 1);
            } else {
                setCreatorRequests([]);
            }
        } catch (err) {
            console.error("Creator requests fetch error:", err);
            setCrError("Failed to load creator requests.");
        } finally {
            setCrLoading(false);
        }
    };

    useEffect(() => {
        fetchCreatorRequests(1, crSearch, crStatusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCreatorRequests(1, crSearch, crStatusFilter);
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crSearch, crStatusFilter]);

    const handleCreatorRequestAction = (action, item) => {
        if (action === "View") {
            setSelectedCreatorReq(item);
            setCrViewModal(true);
            return;
        }

        if (action === "Approve") {
            Swal.fire({
                title: "Approve Creator Request",
                text: `Approve creator request for ${item.userId?.name || "this user"}?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#2dce89",
                cancelButtonColor: "#8898aa",
                confirmButtonText: "Yes, approve",
                cancelButtonText: "Cancel",
            }).then(async (r) => {
                if (!r.isConfirmed) return;
                try {
                    const res = await updateCreatorRequestStatus(item._id, { status: "approved" });
                    if (res?.success) {
                        Swal.fire({ title: "Approved!", text: "Creator request has been approved.", icon: "success", timer: 3000, showConfirmButton: false });
                        fetchCreatorRequests(crPage, crSearch, crStatusFilter);
                        if (selectedCreatorReq?._id === item._id) {
                            setSelectedCreatorReq({ ...item, status: "approved" });
                        }
                    } else {
                        throw new Error(res?.error || "Approval failed");
                    }
                } catch (err) {
                    Swal.fire("Error", err.message || "Failed to approve creator request.", "error");
                }
            });
            return;
        }

        if (action === "Reject") {
            Swal.fire({
                title: "Reject Creator Request",
                html: `<p class="text-sm text-muted mb-2">Provide a reason for rejecting <strong>${item.userId?.name || "this user"}</strong>'s creator request.</p>`,
                input: "textarea",
                inputPlaceholder: "e.g. Incomplete profile, invalid category…",
                inputAttributes: { rows: 3 },
                showCancelButton: true,
                confirmButtonColor: "#f5365c",
                cancelButtonColor: "#8898aa",
                confirmButtonText: "Reject",
                cancelButtonText: "Cancel",
                preConfirm: (reason) => {
                    if (!reason || !reason.trim()) {
                        Swal.showValidationMessage("Rejection reason is required.");
                        return false;
                    }
                    return reason.trim();
                },
            }).then(async (r) => {
                if (!r.isConfirmed) return;
                const rejectedReason = r.value;
                try {
                    const res = await updateCreatorRequestStatus(item._id, { status: "rejected", rejectedReason });
                    if (res?.success) {
                        Swal.fire({ title: "Rejected!", text: "Creator request has been rejected.", icon: "success", timer: 3000, showConfirmButton: false });
                        fetchCreatorRequests(crPage, crSearch, crStatusFilter);
                        if (selectedCreatorReq?._id === item._id) {
                            setSelectedCreatorReq({ ...item, status: "rejected", rejectedReason });
                        }
                    } else {
                        throw new Error(res?.error || "Rejection failed");
                    }
                } catch (err) {
                    Swal.fire("Error", err.message || "Failed to reject creator request.", "error");
                }
            });
            return;
        }
    };

    const renderCrPagination = () => {
        if (crTotalPages <= 1) return null;
        const pages = [];
        for (let i = 1; i <= crTotalPages; i++) {
            pages.push(
                <PaginationItem key={i} active={crPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); fetchCreatorRequests(i, crSearch, crStatusFilter); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }
        return (
            <Pagination className="pagination justify-content-end mb-0">
                <PaginationItem disabled={crPage <= 1}>
                    <PaginationLink onClick={() => fetchCreatorRequests(crPage - 1, crSearch, crStatusFilter)} tabIndex="-1"><i className="fas fa-angle-left" /></PaginationLink>
                </PaginationItem>
                {pages}
                <PaginationItem disabled={crPage >= crTotalPages}>
                    <PaginationLink onClick={() => fetchCreatorRequests(crPage + 1, crSearch, crStatusFilter)}><i className="fas fa-angle-right" /></PaginationLink>
                </PaginationItem>
            </Pagination>
        );
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0" style={styles.glassCard}>
                            <CardHeader className="border-0 bg-transparent py-4">
                                <Row className="align-items-center">
                                    <Col md="6">
                                        <h3 className="mb-0 text-dark font-weight-700">
                                            <i className="ni ni-badge text-info mr-2" /> Creator Management
                                        </h3>
                                    </Col>
                                    <Col md="6" className="text-right">
                                        {activeTab === "types" && (
                                            <Button color="primary" onClick={() => { setEditingItem(null); setFormData({ type: "", type_name: "" }); setModal(true); }} style={{ borderRadius: "10px" }}>
                                                <i className="fas fa-plus-circle mr-2" /> Add Creator Type
                                            </Button>
                                        )}
                                        {activeTab === "requests" && (
                                            <div className="d-inline-flex align-items-center" style={{ gap: "10px" }}>
                                                <InputGroup style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #e2e8f0", background: "#fff", width: "220px" }}>
                                                    <InputGroupAddon addonType="prepend">
                                                        <InputGroupText style={{ background: "#fff", border: "none" }}><i className="fas fa-search" style={{ color: "#8898aa", fontSize: "13px" }} /></InputGroupText>
                                                    </InputGroupAddon>
                                                    <Input placeholder="Search Requests..." value={crSearch} onChange={e => setCrSearch(e.target.value)} style={{ border: "none", boxShadow: "none", fontSize: "13px" }} />
                                                </InputGroup>
                                                <Input type="select" value={crStatusFilter} onChange={e => setCrStatusFilter(e.target.value)} style={{ borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "13px", color: "#525f7f", width: "140px" }}>
                                                    <option value="All">All Statuses</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Approved">Approved</option>
                                                    <option value="Rejected">Rejected</option>
                                                </Input>
                                            </div>
                                        )}
                                        {activeTab === "eligibility" && (
                                            <Button
                                                color="primary"
                                                onClick={saveCreatorConfig}
                                                disabled={savingConfig}
                                                style={{ borderRadius: "10px" }}
                                            >
                                                {savingConfig ? <i className="fas fa-spinner fa-spin mr-2" /> : <i className="fas fa-save mr-2" />}
                                                Save Config
                                            </Button>
                                        )}
                                    </Col>
                                </Row>

                                {/* Tab Navigation */}
                                <Nav tabs className="mt-3 border-0">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "types" })}
                                            onClick={() => setActiveTab("types")}
                                            style={{
                                                ...styles.tabNav,
                                                color: activeTab === "types" ? "#5e72e4" : "#8898aa",
                                                borderBottom: activeTab === "types" ? "3px solid #5e72e4" : "3px solid transparent",
                                                background: activeTab === "types" ? "rgba(94,114,228,0.05)" : "transparent",
                                            }}
                                        >
                                            <i className="fas fa-layer-group mr-2" /> Creator Types
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "requests" })}
                                            onClick={() => setActiveTab("requests")}
                                            style={{
                                                ...styles.tabNav,
                                                color: activeTab === "requests" ? "#5e72e4" : "#8898aa",
                                                borderBottom: activeTab === "requests" ? "3px solid #5e72e4" : "3px solid transparent",
                                                background: activeTab === "requests" ? "rgba(94,114,228,0.05)" : "transparent",
                                            }}
                                        >
                                            <i className="fas fa-user-plus mr-2" /> Creator Requests
                                            {crTotal > 0 && (
                                                <Badge color="primary" pill className="ml-2" style={{ fontSize: "11px" }}>{crTotal}</Badge>
                                            )}
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "eligibility" })}
                                            onClick={() => setActiveTab("eligibility")}
                                            style={{
                                                ...styles.tabNav,
                                                color: activeTab === "eligibility" ? "#5e72e4" : "#8898aa",
                                                borderBottom: activeTab === "eligibility" ? "3px solid #5e72e4" : "3px solid transparent",
                                                background: activeTab === "eligibility" ? "rgba(94,114,228,0.05)" : "transparent",
                                            }}
                                        >
                                            <i className="fas fa-sliders-h mr-2" /> Eligibility Config
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </CardHeader>

                            <CardBody className="p-0">
                                <TabContent activeTab={activeTab}>
                                    {/* ── Creator Types Tab ── */}
                                    <TabPane tabId="types">
                                        <div className="table-responsive">
                                            <Table className="align-items-center table-flush" hover>
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th scope="col" style={{ padding: "16px 24px" }}>Type Name</th>
                                                        <th scope="col">Type</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Added On</th>
                                                        <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {loading && creators.length === 0 ? (
                                                        <tr><td colSpan="5" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>
                                                    ) : creators.length === 0 ? (
                                                        <tr><td colSpan="5" className="text-center py-5 text-muted">No creator types found.</td></tr>
                                                    ) : (
                                                        creators.map((item) => (
                                                            <tr key={item.c_uuid || item._id} style={styles.tableRow}>
                                                                <td style={{ padding: "16px 24px" }}><span className="font-weight-600 text-dark">{item.type_name}</span></td>
                                                                <td>{item.type?.toUpperCase()}</td>
                                                                <td>
                                                                    <Badge
                                                                        color={item.type_isactive ? "success" : "danger"}
                                                                        pill
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handleToggleStatus(item.c_uuid || item._id)}
                                                                    >
                                                                        {item.type_isactive ? "Active" : "Inactive"}
                                                                    </Badge>
                                                                </td>
                                                                <td>{item.created_at && !isNaN(new Date(item.created_at).getTime()) ? new Date(item.created_at).toLocaleDateString() : "N/A"}</td>
                                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                                    <UncontrolledDropdown>
                                                                        <DropdownToggle className="btn-icon-only" role="button" size="sm" color="">
                                                                            <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                                        </DropdownToggle>
                                                                        <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                            <DropdownItem onClick={() => handleEdit(item)}>Edit</DropdownItem>
                                                                            <DropdownItem onClick={() => handleToggleStatus(item.c_uuid || item._id)}>
                                                                                {item.type_isactive ? "Deactivate" : "Activate"}
                                                                            </DropdownItem>
                                                                            <DropdownItem divider />
                                                                            <DropdownItem onClick={() => handleDelete(item.c_uuid || item._id)} className="text-danger">Delete</DropdownItem>
                                                                        </DropdownMenu>
                                                                    </UncontrolledDropdown>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </TabPane>

                                    {/* ── Creator Requests Tab ── */}
                                    <TabPane tabId="requests">
                                        <div className="table-responsive" style={{ width: "100%", overflowX: "auto" }}>
                                            <Table className="align-items-center table-flush" hover>
                                                <thead className="thead-light">
                                                    <tr>
                                                        <th scope="col" style={{ padding: "16px 24px" }}>User</th>
                                                        <th scope="col">Category</th>
                                                        <th scope="col">Experience</th>
                                                        <th scope="col">Services</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Submitted On</th>
                                                        <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {crLoading ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-5">
                                                                <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                                                                <div className="text-muted mt-2" style={{ fontSize: "13px" }}>Loading creator requests…</div>
                                                            </td>
                                                        </tr>
                                                    ) : crError ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-5 text-danger">
                                                                <i className="fas fa-exclamation-circle fa-2x mb-2" />
                                                                <div>{crError}</div>
                                                                <Button size="sm" color="primary" className="mt-2" onClick={() => fetchCreatorRequests(crPage, crSearch, crStatusFilter)}>Retry</Button>
                                                            </td>
                                                        </tr>
                                                    ) : creatorRequests.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-5 text-muted">
                                                                <i className="fas fa-user-plus fa-2x mb-2" style={{ display: "block" }} />
                                                                <div>No creator requests found.</div>
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        creatorRequests.map((item) => {
                                                            const statusLabel = item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "Pending";
                                                            const submittedDate = item.createdAt
                                                                ? new Date(item.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                                                : "—";
                                                            return (
                                                                <tr key={item._id} style={styles.tableRow}>
                                                                    <td style={{ padding: "16px 24px" }}>
                                                                        <div className="d-flex align-items-center">
                                                                            {(() => {
                                                                                const rawImage = item?.userId?.image;
                                                                                let imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(item?.userId?.name || "User")}&background=random&color=fff`;
                                                                                if (rawImage) {
                                                                                    const fileName = rawImage.split("/").pop();
                                                                                    imageUrl = `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${fileName}`;
                                                                                }
                                                                                return (
                                                                                    <img
                                                                                        src={imageUrl}
                                                                                        alt={item?.userId?.name || "User"}
                                                                                        className="rounded-circle mr-3"
                                                                                        style={{ width: "40px", height: "40px", minWidth: "40px", objectFit: "cover", border: "1px solid #e9ecef" }}
                                                                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item?.userId?.name || "User")}&background=random&color=fff`; }}
                                                                                    />
                                                                                );
                                                                            })()}
                                                                            <div className="d-flex flex-column">
                                                                                <span className="font-weight-bold text-dark">{item?.userId?.name || "—"}</span>
                                                                                <span className="text-xs text-muted">{item?.userId?.email || "—"}</span>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td><span className="font-weight-bold">{item.categoryType || "—"}</span></td>
                                                                    <td><Badge color="light" style={{ color: "#525f7f", padding: "5px 10px", borderRadius: "6px" }}>{item.experience || "—"}</Badge></td>
                                                                    <td style={{ minWidth: "220px", verticalAlign: "top" }}>
                                                                        <div
                                                                            className="d-flex flex-wrap"
                                                                            style={{
                                                                                gap: "6px",
                                                                                maxWidth: "220px",
                                                                            }}
                                                                        >
                                                                            {(item.servicesType || []).slice(0, 2).map((s, i) => (
                                                                                <Badge
                                                                                    key={i}
                                                                                    color="primary"
                                                                                    style={{
                                                                                        padding: "5px 10px",
                                                                                        borderRadius: "6px",
                                                                                        fontSize: "11px",
                                                                                        whiteSpace: "nowrap",
                                                                                    }}
                                                                                >
                                                                                    {s}
                                                                                </Badge>
                                                                            ))}

                                                                            {(item.servicesType || []).length > 2 && (
                                                                                <Badge
                                                                                    color="secondary"
                                                                                    style={{
                                                                                        padding: "5px 10px",
                                                                                        borderRadius: "6px",
                                                                                        fontSize: "11px",
                                                                                        color: "#b7afaf",
                                                                                        whiteSpace: "nowrap",
                                                                                    }}
                                                                                >
                                                                                    +{item.servicesType.length - 2}
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                    </td>

                                                                    <td
                                                                        style={{
                                                                            minWidth: "120px",
                                                                            verticalAlign: "middle",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {getStatusBadge(statusLabel)}
                                                                    </td>
                                                                    <td className="text-muted text-sm">{submittedDate}</td>
                                                                    <td className="text-right" style={{ paddingRight: "24px" }}>
                                                                        <div className="d-flex justify-content-end align-items-center">
                                                                            <Button
                                                                                color="info"
                                                                                outline
                                                                                size="sm"
                                                                                className="px-3 mr-2"
                                                                                style={{ borderRadius: "8px", fontSize: "12px", fontWeight: 600, height: "32px" }}
                                                                                onClick={() => handleCreatorRequestAction("View", item)}
                                                                            >
                                                                                <i className="fas fa-eye mr-2" /> View
                                                                            </Button>
                                                                            <UncontrolledDropdown>
                                                                                <DropdownToggle
                                                                                    className="btn-icon-only shadow-sm"
                                                                                    role="button"
                                                                                    size="sm"
                                                                                    style={{ background: "#fff", border: "none", borderRadius: "8px", color: "#6c757d", width: "32px", height: "32px", padding: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
                                                                                    onClick={(e) => e.preventDefault()}
                                                                                >
                                                                                    <i className="fas fa-ellipsis-v" />
                                                                                </DropdownToggle>
                                                                                <DropdownMenu className="dropdown-menu-arrow shadow border-0" right>
                                                                                    {item.status !== "rejected" && (
                                                                                        <DropdownItem onClick={() => handleCreatorRequestAction("Approve", item)} disabled={item.status === "approved"}>
                                                                                            <i className="fas fa-check-circle text-success mr-2" /> {item.status === "approved" ? "Approved" : "Approve"}
                                                                                        </DropdownItem>
                                                                                    )}
                                                                                    {item.status !== "approved" && (
                                                                                        <DropdownItem onClick={() => handleCreatorRequestAction("Reject", item)} disabled={item.status === "rejected"}>
                                                                                            <i className="fas fa-times-circle text-danger mr-2" /> {item.status === "rejected" ? "Rejected" : "Reject"}
                                                                                        </DropdownItem>
                                                                                    )}
                                                                                </DropdownMenu>
                                                                            </UncontrolledDropdown>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </TabPane>
                                    <TabPane tabId="eligibility">
                                        <div className="p-4" style={{ background: "#fff", borderRadius: "0 0 16px 16px" }}>
                                            {creatorConfig ? (
                                                <Row>
                                                    <Col md="4">
                                                        <FormGroup>
                                                            <label className="form-control-label">Min Reputation Score</label>
                                                            <Input
                                                                type="number"
                                                                value={creatorConfig.minReputationScore}
                                                                min="0"
                                                                onChange={(e) => {
                                                                    setCreatorConfig({
                                                                        ...creatorConfig,
                                                                        minReputationScore: parseInt(e.target.value) || 0
                                                                    });
                                                                    if (configErrors.minReputationScore) setConfigErrors({ ...configErrors, minReputationScore: null });
                                                                }}
                                                                style={{ borderRadius: "8px", minHeight: "45px" }}
                                                                invalid={!!configErrors.minReputationScore}
                                                            />
                                                            {configErrors.minReputationScore && <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>{configErrors.minReputationScore}</div>}
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="4">
                                                        <FormGroup>
                                                            <label className="form-control-label">Min Reputation Tier</label>
                                                            <Input
                                                                type="select"
                                                                value={creatorConfig.minReputationTier}
                                                                onChange={(e) => setCreatorConfig({
                                                                    ...creatorConfig,
                                                                    minReputationTier: e.target.value
                                                                })}
                                                                style={{ borderRadius: "8px", minHeight: "45px" }}
                                                            >
                                                                {tierConfig && tierConfig.map((tier) => (
                                                                    <option key={tier.name} value={tier.name}>
                                                                        {tier.name}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="4">
                                                        <FormGroup>
                                                            <label className="form-control-label">Min Joined Rooms</label>
                                                            <Input
                                                                type="number"
                                                                value={creatorConfig.minJoinedRooms}
                                                                min="0"
                                                                onChange={(e) => {
                                                                    setCreatorConfig({
                                                                        ...creatorConfig,
                                                                        minJoinedRooms: parseInt(e.target.value) || 0
                                                                    });
                                                                    if (configErrors.minJoinedRooms) setConfigErrors({ ...configErrors, minJoinedRooms: null });
                                                                }}
                                                                style={{ borderRadius: "8px", minHeight: "45px" }}
                                                                invalid={!!configErrors.minJoinedRooms}
                                                            />
                                                            {configErrors.minJoinedRooms && <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>{configErrors.minJoinedRooms}</div>}
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="4">
                                                        <FormGroup>
                                                            <label className="form-control-label">Min Created Posts</label>
                                                            <Input
                                                                type="number"
                                                                value={creatorConfig.minCreatedPosts}
                                                                min="0"
                                                                onChange={(e) => {
                                                                    setCreatorConfig({
                                                                        ...creatorConfig,
                                                                        minCreatedPosts: parseInt(e.target.value) || 0
                                                                    });
                                                                    if (configErrors.minCreatedPosts) setConfigErrors({ ...configErrors, minCreatedPosts: null });
                                                                }}
                                                                style={{ borderRadius: "8px", minHeight: "45px" }}
                                                                invalid={!!configErrors.minCreatedPosts}
                                                            />
                                                            {configErrors.minCreatedPosts && <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>{configErrors.minCreatedPosts}</div>}
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="4" className="d-flex align-items-center">
                                                        <FormGroup check className="mt-2">
                                                            <Label check style={{ fontWeight: 600, color: "#32325d" }}>
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={creatorConfig.requireKyc}
                                                                    onChange={(e) => setCreatorConfig({
                                                                        ...creatorConfig,
                                                                        requireKyc: e.target.checked
                                                                    })}
                                                                />{" "}
                                                                Require KYC Approval
                                                            </Label>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md="4" className="d-flex align-items-center">
                                                        <FormGroup check className="mt-2">
                                                            <Label check style={{ fontWeight: 600, color: "#32325d" }}>
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={creatorConfig.requireBadge}
                                                                    onChange={(e) => setCreatorConfig({
                                                                        ...creatorConfig,
                                                                        requireBadge: e.target.checked
                                                                    })}
                                                                />{" "}
                                                                Require Earned Badge
                                                            </Label>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                                                    <p className="mt-2 text-muted">Loading settings...</p>
                                                </div>
                                            )}
                                        </div>
                                    </TabPane>
                                </TabContent>
                            </CardBody>

                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                {activeTab === "types" && (
                                    <>
                                        <span className="text-muted small">
                                            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
                                        </span>
                                        {renderPagination()}
                                    </>
                                )}
                                {activeTab === "requests" && (
                                    <>
                                        <span className="text-muted small">
                                            Showing {crTotal > 0 ? ((crPage - 1) * CR_LIMIT) + 1 : 0} to {Math.min(crPage * CR_LIMIT, crTotal)} of {crTotal} requests
                                        </span>
                                        {renderCrPagination()}
                                    </>
                                )}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ── Add/Edit Creator Type Modal ── */}
            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle} className="border-0">
                    {editingItem ? "Edit Creator Type" : "Add New Creator Type"}
                </ModalHeader>
                <ModalBody>
                    <form noValidate onSubmit={handleSubmit}>
                        <FormGroup>
                            <label className="form-control-label">Type Code <span className="text-danger">*</span></label>
                            {/* Hidden input for form validation */}
                            <input type="hidden" required value={formData.type} />
                            <div ref={typeDropdownRef} style={{ position: "relative" }}>
                                <div
                                    onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                                    style={{
                                        borderRadius: "8px",
                                        minHeight: "45px",
                                        border: typeDropdownOpen ? "1.5px solid #5e72e4" : "1px solid #dee2e6",
                                        padding: "10px 14px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        cursor: "pointer",
                                        background: "#fff",
                                        transition: "all 0.2s ease",
                                        boxShadow: typeDropdownOpen ? "0 0 0 3px rgba(94,114,228,0.15)" : "none",
                                    }}
                                >
                                    <span style={{ color: formData.type ? "#32325d" : "#adb5bd", fontSize: "14px", fontWeight: formData.type ? 500 : 400 }}>
                                        {formData.type?.toUpperCase() || "Select Type Code"}
                                    </span>
                                    <i
                                        className="fas fa-chevron-down"
                                        style={{
                                            fontSize: "12px",
                                            color: "#8898aa",
                                            transition: "transform 0.2s ease",
                                            transform: typeDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                                        }}
                                    />
                                </div>
                                {typeDropdownOpen && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "calc(100% + 4px)",
                                            left: 0,
                                            right: 0,
                                            background: "#fff",
                                            borderRadius: "10px",
                                            border: "1px solid #e9ecef",
                                            boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                                            zIndex: 1050,
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            animation: "fadeInDown 0.15s ease",
                                        }}
                                    >
                                        {typeCodes.length === 0 ? (
                                            <div style={{ padding: "14px 16px", color: "#8898aa", fontSize: "13px", textAlign: "center" }}>
                                                <i className="fas fa-info-circle mr-2" />No type codes available
                                            </div>
                                        ) : (
                                            typeCodes.map((tc) => (
                                                <div
                                                    key={tc.code}
                                                    onClick={() => {
                                                        setFormData({ ...formData, type: tc.code });
                                                        setTypeDropdownOpen(false);
                                                    }}
                                                    style={{
                                                        padding: "10px 16px",
                                                        cursor: "pointer",
                                                        fontSize: "14px",
                                                        color: formData.type === tc.code ? "#5e72e4" : "#32325d",
                                                        fontWeight: formData.type === tc.code ? 600 : 400,
                                                        background: formData.type === tc.code ? "rgba(94,114,228,0.08)" : "transparent",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        transition: "background 0.15s ease",
                                                        borderBottom: "1px solid #f6f9fc",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (formData.type !== tc.code) e.currentTarget.style.background = "#f8f9fe";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (formData.type !== tc.code) e.currentTarget.style.background = "transparent";
                                                    }}
                                                >
                                                    <span>{tc.label?.toUpperCase()}</span>
                                                    {formData.type === tc.code && (
                                                        <i className="fas fa-check" style={{ fontSize: "12px", color: "#5e72e4" }} />
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <label className="form-control-label">Type Name (e.g. Individual) <span className="text-danger">*</span></label>
                            <Input
                                type="text"
                                placeholder="e.g. Individual"
                                required
                                value={formData.type_name}
                                onChange={(e) => setFormData({ ...formData, type_name: e.target.value })}
                                style={{ borderRadius: "8px", minHeight: "45px" }}
                            />
                        </FormGroup>
                        <Button color="primary" block type="submit" style={{ minHeight: "45px", borderRadius: "10px" }}>
                            {editingItem ? "Update" : "Save"} Creator Type
                        </Button>
                    </form>
                </ModalBody>
            </Modal>

            {/* ── Creator Request View Modal ── */}
            {selectedCreatorReq && (
                <Modal isOpen={crViewModal} toggle={() => setCrViewModal(false)} size="lg" centered>
                    <ModalHeader toggle={() => setCrViewModal(false)} className="border-0 pb-0" style={{ background: "linear-gradient(135deg, #f8f9fe 0%, #eef1f8 100%)" }}>
                        <div className="d-flex align-items-center">
                            <span className="font-weight-bold" style={{ fontSize: "18px" }}>
                                <i className="fas fa-user-plus text-primary mr-2" /> Creator Request Details
                            </span>
                            <span className="ml-3">{getStatusBadge(selectedCreatorReq.status || "pending")}</span>
                        </div>
                    </ModalHeader>
                    <ModalBody className="p-0">
                        <Row className="m-0">
                            {/* Left Side — Details */}
                            <Col md="8" className="p-4 bg-white" style={{ borderBottomLeftRadius: "16px" }}>
                                {/* User Info */}
                                <div style={styles.infoBlock}>
                                    <div className="text-muted text-xs text-uppercase font-weight-bold mb-2">Applicant</div>
                                    <div className="d-flex align-items-center">
                                        {(() => {
                                            const rawImage = selectedCreatorReq?.userId?.image;
                                            let imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCreatorReq?.userId?.name || "User")}&background=random&color=fff`;
                                            if (rawImage) {
                                                const fileName = rawImage.split("/").pop();
                                                imageUrl = `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${fileName}`;
                                            }
                                            return (
                                                <img
                                                    src={imageUrl}
                                                    alt={selectedCreatorReq?.userId?.name || "User"}
                                                    className="rounded-circle mr-3"
                                                    style={{ width: "48px", height: "48px", objectFit: "cover", border: "2px solid #e9ecef" }}
                                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCreatorReq?.userId?.name || "User")}&background=random&color=fff`; }}
                                                />
                                            );
                                        })()}
                                        <div>
                                            <div className="font-weight-bold text-dark" style={{ fontSize: "15px" }}>{selectedCreatorReq?.userId?.name || "—"}</div>
                                            <div className="text-muted text-sm">{selectedCreatorReq?.userId?.email || "—"}</div>
                                            {selectedCreatorReq?.userId?.country && (
                                                <div className="text-muted text-xs mt-1"><i className="fas fa-globe-americas mr-1" />{selectedCreatorReq.userId.country}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Request Details Grid */}
                                <Row>
                                    <Col md="6">
                                        <div style={styles.infoBlock}>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Category Type</div>
                                            <div className="font-weight-bold text-dark mt-1">
                                                <i className="fas fa-layer-group text-primary mr-2" />{selectedCreatorReq.categoryType || "—"}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div style={styles.infoBlock}>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Experience Level</div>
                                            <div className="font-weight-bold text-dark mt-1">
                                                <i className="fas fa-chart-line text-info mr-2" />{selectedCreatorReq.experience || "—"}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Services */}
                                <div style={styles.infoBlock}>
                                    <div className="text-muted text-xs text-uppercase font-weight-bold mb-2">Service Types</div>
                                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                                        {(selectedCreatorReq.servicesType || []).map((s, i) => (
                                            <Badge key={i} color="primary" style={{ padding: "6px 12px", borderRadius: "8px", fontSize: "12px" }}>{s}</Badge>
                                        ))}
                                        {(!selectedCreatorReq.servicesType || selectedCreatorReq.servicesType.length === 0) && (
                                            <span className="text-muted text-sm">No services listed</span>
                                        )}
                                    </div>
                                </div>

                                {/* Content Languages */}
                                <div style={styles.infoBlock}>
                                    <div className="text-muted text-xs text-uppercase font-weight-bold mb-2">Content Languages</div>
                                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                                        {(selectedCreatorReq.contentLanguage || []).map((lang, i) => (
                                            <Badge key={i} color="info" style={{ padding: "6px 12px", borderRadius: "8px", fontSize: "12px" }}>{lang}</Badge>
                                        ))}
                                        {(!selectedCreatorReq.contentLanguage || selectedCreatorReq.contentLanguage.length === 0) && (
                                            <span className="text-muted text-sm">No languages listed</span>
                                        )}
                                    </div>
                                </div>

                                {/* Bio */}
                                <div style={styles.infoBlock}>
                                    <div className="text-muted text-xs text-uppercase font-weight-bold mb-2">Bio</div>
                                    <p className="text-dark mb-0" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                                        {selectedCreatorReq.bio || "No bio provided."}
                                    </p>
                                </div>

                                {/* Rejection Reason (if rejected) */}
                                {selectedCreatorReq.status === "rejected" && selectedCreatorReq.rejectedReason && (
                                    <div style={{ ...styles.infoBlock, borderColor: "#f5365c", background: "#fff5f5" }}>
                                        <div className="text-danger text-xs text-uppercase font-weight-bold mb-1">
                                            <i className="fas fa-ban mr-1" /> Rejection Reason
                                        </div>
                                        <p className="text-dark mb-0" style={{ fontSize: "14px" }}>
                                            {selectedCreatorReq.rejectedReason}
                                        </p>
                                    </div>
                                )}

                                {/* Timestamps */}
                                <Row>
                                    <Col md="6">
                                        <div style={styles.infoBlock}>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Submitted On</div>
                                            <div className="text-dark text-sm">
                                                {selectedCreatorReq.createdAt ? new Date(selectedCreatorReq.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div style={styles.infoBlock}>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Last Updated</div>
                                            <div className="text-dark text-sm">
                                                {selectedCreatorReq.updatedAt ? new Date(selectedCreatorReq.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Right Side — Actions */}
                            <Col md="4" className="p-4 pt-5" style={{ background: "#f8f9fe" }}>
                                {selectedCreatorReq.status === "pending" && (
                                    <>
                                        <h6 className="text-uppercase text-muted ls-1 mb-3">Quick Actions</h6>
                                        <Button
                                            block
                                            color="success"
                                            className="mb-3 text-left"
                                            onClick={() => { setCrViewModal(false); handleCreatorRequestAction("Approve", selectedCreatorReq); }}
                                            style={{ borderRadius: "10px", fontWeight: 600 }}
                                        >
                                            <i className="fas fa-check-circle mr-2" /> Approve Request
                                        </Button>

                                        <Button
                                            block
                                            color="danger"
                                            className="mb-3 text-left"
                                            outline
                                            onClick={() => { setCrViewModal(false); handleCreatorRequestAction("Reject", selectedCreatorReq); }}
                                            style={{ borderRadius: "10px", fontWeight: 600 }}
                                        >
                                            <i className="fas fa-times-circle mr-2" /> Reject Request
                                        </Button>
                                        <hr className="my-4 border-light" />
                                    </>
                                )}

                                <div style={styles.infoBlock}>
                                    <div className="text-muted text-xs text-uppercase font-weight-bold mb-2">Terms Agreement</div>
                                    <div className="d-flex align-items-center">
                                        <i className={`fas ${selectedCreatorReq.termsAgree ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} mr-2`} style={{ fontSize: "18px" }} />
                                        <span className="font-weight-bold text-dark">{selectedCreatorReq.termsAgree ? "Accepted" : "Not Accepted"}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </ModalBody>
                </Modal>
            )}
        </>
    );
};

export default Creator;
